import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LanguageIcon from '@mui/icons-material/Language';
import { useLanguage } from '../contexts/LanguageContext';
import mainLogo from '../assets/logos/main_logo.jpeg';
import darkModeLogo from '../assets/logos/dark_mode_logo.jpeg';

interface HeaderProps {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const LOGO_SIZE = 80; // Use consistent size for both modes

const Header: React.FC<HeaderProps> = ({ mode, toggleTheme }) => {
  const { currentLanguage, setLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang: 'en' | 'pt') => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  // Show main_logo.jpeg for light mode, dark_mode_logo.jpeg for dark mode
  const logoSrc = mode === 'light' ? darkModeLogo : mainLogo;

  return (
    <AppBar position="static" color="transparent" elevation={0}>
      <Toolbar>
        <div
          style={{
            height: LOGO_SIZE,
            width: LOGO_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            marginRight: 16,
            position: 'relative',
          }}
        >
          <img
            src={logoSrc}
            alt="Crafticore Logo"
            style={{
              height: '70px', // Fixed height so both logos appear the same size
              width: 'auto', // Maintain aspect ratio
              objectFit: 'contain',
              display: 'block',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </div>
        <Typography variant="h5" color="inherit" noWrap sx={{ flexGrow: 1 }}>
          Crafticore
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button color="inherit" component={RouterLink} to="/">
            {t('nav.home')}
          </Button>
          <Button color="inherit" component={RouterLink} to="/portfolio">
            {t('nav.portfolio')}
          </Button>
          <Button color="inherit" component={RouterLink} to="/contact">
            {t('nav.contact')}
          </Button>
          <IconButton
            onClick={handleLanguageMenuOpen}
            color="inherit"
            sx={{ ml: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageMenuClose}
          >
            <MenuItem
              onClick={() => handleLanguageChange('en')}
              selected={currentLanguage === 'en'}
            >
              English
            </MenuItem>
            <MenuItem
              onClick={() => handleLanguageChange('pt')}
              selected={currentLanguage === 'pt'}
            >
              Português
            </MenuItem>
          </Menu>
          <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 1 }}>
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 